import { CapacityBar } from "@/lib/capacity";
import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, Gauge, AlertTriangle } from "lucide-react";
import { TagChip, fmtMinutes, DuePicker, UserAvatar } from "@/components/TaskShared";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const pad = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (iso) => { const d = new Date(iso + "T00:00:00"); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd); return isoOf(d); };
const addDaysIso = (iso, n) => { const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + n); return isoOf(d); };
const hhmmToMin = (s) => { const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(s || ""); return m ? +m[1] * 60 + +m[2] : null; };
const min12 = (m) => { let h = Math.floor(m / 60), mm = m % 60; const ap = h >= 12 ? "pm" : "am"; h = h % 12 || 12; return mm ? `${h}:${pad(mm)}${ap}` : `${h}${ap}`; };

function statusColor(t) {
  if (t.is_completed || t.status === "Completed") return { bar: "#30BD79", text: "#146c43" };
  if (t.status === "To Do") return { bar: "#9296A0", text: "#3d4148" };
  return { bar: "#723FFF", text: "#4a2bb0" };
}

export function WeekAgenda({ tasks, me, canEdit = true, onOpenTask, onReschedule, capacity }) {
  const [weekStart, setWeekStart] = useState(() => mondayOf(isoOf(new Date())));
  const todayIso = isoOf(new Date());
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDaysIso(weekStart, i)), [weekStart]);

  const byDay = useMemo(() => {
    const map = {};
    weekDays.forEach((d) => (map[d] = []));
    (tasks || []).forEach((t) => {
      if (!t.due_date || t.is_completed) return;
      if (!(t.due_date in map)) return;
      map[t.due_date].push(t);
    });
    Object.keys(map).forEach((d) => map[d].sort((a, b) => (hhmmToMin(a.start_time) ?? 9999) - (hhmmToMin(b.start_time) ?? 9999)));
    return map;
  }, [tasks, weekDays]);

  const dayLoad = useMemo(() => {
    const cap = me?.capacity_days || {};
    const m = {};
    weekDays.forEach((d, i) => {
      const scheduled = (byDay[d] || []).reduce((s, t) => s + (t.time_estimate_minutes || 0), 0);
      const capMin = Math.round((cap[DAY_KEYS[i]] || 0) * 60);
      m[d] = { scheduled, capMin, over: scheduled > 0 && scheduled > capMin };
    });
    return m;
  }, [byDay, weekDays, me]);

  const monthLabel = () => {
    const a = new Date(weekStart + "T00:00:00"), b = new Date(weekDays[6] + "T00:00:00");
    const f = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
    return `${f(a)} – ${f(b)}, ${b.getFullYear()}`;
  };

  const total = weekDays.reduce((s, d) => s + (byDay[d] || []).length, 0);

  return (
    <div data-testid="week-agenda">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="agenda-prev" onClick={() => setWeekStart(addDaysIso(weekStart, -7))} className="h-8 w-8 rounded-full border border-[#EEEFEF] bg-white flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="agenda-today" onClick={() => setWeekStart(mondayOf(isoOf(new Date())))} className="h-8 px-3 rounded-full border border-[#EEEFEF] bg-white text-xs font-medium text-[#595D62] hover:bg-[#F6F6FD]">Today</button>
          <button data-testid="agenda-next" onClick={() => setWeekStart(addDaysIso(weekStart, 7))} className="h-8 w-8 rounded-full border border-[#EEEFEF] bg-white flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><ChevronRight className="h-4 w-4" /></button>
          <span data-testid="agenda-label" className="ml-1 font-display text-lg font-semibold text-[#06060C]">{monthLabel()}</span>
        </div>
        <p className="text-xs text-[#9FA1A4]">{total} task{total === 1 ? "" : "s"} this week</p>
      </div>

      <div className="space-y-3">
        {weekDays.map((d, i) => {
          const rows = byDay[d] || [];
          const dd = new Date(d + "T00:00:00");
          const isToday = d === todayIso;
          const ld = dayLoad[d] || {};
          return (
            <div key={d} data-testid={`agenda-day-${d}`} className="rounded-2xl border border-[#EEEFEF] bg-white overflow-hidden">
              <div className={`flex items-center justify-between px-4 py-2.5 border-b border-[#f0f0f3] ${isToday ? "bg-[#F6F2FF]" : "bg-[#FCFCFE]"}`}>
                <div className="flex items-baseline gap-2">
                  <span className={`text-sm font-semibold ${isToday ? "text-[#5b2fd6]" : "text-[#06060C]"}`}>{DAY_LABELS[i]}</span>
                  <span className="font-mono-data text-[11px] text-[#9FA1A4]">{dd.toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
                  {isToday && <span className="text-[9px] font-mono-data uppercase tracking-wider text-[#5b2fd6] bg-white rounded-full px-1.5 py-0.5 border border-[#e6dcff]">Today</span>}
                </div>
                {capacity && (() => { const c = capacity.get(d); return <CapacityBar used={c.used} cap={c.cap} testid={`agenda-capacity-${d}`} />; })()}
                {!capacity && rows.length > 0 && (
                  <span data-testid={`agenda-load-${d}`} className={`inline-flex items-center gap-1 text-[10px] font-semibold leading-none ${ld.over ? "text-[#c0392b]" : "text-[#9FA1A4]"}`}>
                    {ld.over && <AlertTriangle className="h-2.5 w-2.5" />}{fmtMinutes(ld.scheduled)}{ld.capMin ? ` / ${fmtMinutes(ld.capMin)}` : ""}
                  </span>
                )}
              </div>
              {rows.length === 0 ? (
                <div className="px-4 py-3 text-xs text-[#c4c5cc]">Nothing scheduled</div>
              ) : (
                <div className="divide-y divide-[#f4f4f7]">
                  {rows.map((t) => {
                    const c = statusColor(t);
                    const startMin = hhmmToMin(t.start_time);
                    return (
                      <div key={t.id} data-testid={`agenda-task-${t.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#FAFAFE] transition-colors">
                        <span className="w-1 self-stretch rounded-full shrink-0" style={{ background: c.bar }} />
                        <button data-testid={`agenda-open-${t.id}`} onClick={() => onOpenTask && onOpenTask(t.id)} className="flex-1 min-w-0 text-left">
                          <div className="flex items-center gap-2">
                            <span className="font-mono-data text-[11px] text-[#5b2fd6] shrink-0">{t.code}</span>
                            <span className="text-sm text-[#06060C] truncate">{t.title}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {startMin != null && <span className="font-mono-data text-[10px] text-[#9FA1A4] flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{min12(startMin)}</span>}
                            {t.time_estimate_minutes ? <span className="font-mono-data text-[10px] text-[#9FA1A4] flex items-center gap-0.5"><Gauge className="h-2.5 w-2.5" />{fmtMinutes(t.time_estimate_minutes)}</span> : null}
                            {t.tag && <TagChip tag={t.tag} />}
                            {t.client?.name && <span className="text-[10px] text-[#9296A0] truncate">{t.project_name ? `${t.project_name} · ` : ""}{t.client.name}</span>}
                          </div>
                        </button>
                        {t.assignee && <UserAvatar name={t.assignee.name} uid={t.assignee.id} hasAvatar={t.assignee.has_avatar} size={22} />}
                        <div className="w-40 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <DuePicker compact testid={`agenda-due-${t.id}`} value={t.due_date || ""} disabled={!canEdit} allowClear={false} onChange={(v) => v && onReschedule && onReschedule(t.id, { due_date: v })} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
